import {
    Autocomplete,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers';
import { useAppDispatch } from '../../../store/store';
import { fetchAllCPTCodes, saveCPTCode, setSelectedIds } from '../../../store/adminCPTCodesSlice.slice';
import { CPTcodeCategoryEnum } from '../../../Enum/CPTcodeCategoryEnum';
import EditIcon from '@mui/icons-material/Edit';
import { DataGrid, gridClasses, GridColDef } from '@mui/x-data-grid';
import CancelIcon from '@mui/icons-material/Cancel';
import { Form } from 'react-router-dom';
import ConfirmDelete from '../../Shared/ConfirmDelete';
import { CPTcode } from '../../../Models/Shared/CPTCodeGroup';
import { fetchActiveModifiers } from '../../../store/adminCPTModifiersSlice.slice';

interface CPTCodesProps {
    open: boolean;
    onClose: () => void;
    title: string;
    selectedCPTCode: CPTcode | null;
    setSelectedCPTCode: (cptCode: CPTcode | null) => void;
}

const EditCPTCodeModal = (props: CPTCodesProps) => {
    const dispatch = useAppDispatch();
    const { placesOfEncounters } = useSelector((state: RootState) => state.adminCPTCodesSlice);
    const { modifiers } = useSelector((state: RootState) => state.adminCPTModifiersSlice);

    const CPTcodeCategories = [
        { value: CPTcodeCategoryEnum.ECG, label: 'ECG' },
        { value: CPTcodeCategoryEnum.EvaluationAndManagement, label: 'Evaluation And Management' },
        { value: CPTcodeCategoryEnum.FollowUpVisit, label: 'Follow Up Visit' },
        { value: CPTcodeCategoryEnum.General, label: 'General' },
        { value: CPTcodeCategoryEnum.InitialVisit, label: 'Initial Visit' },
        { value: CPTcodeCategoryEnum.IVHydration, label: 'IV Hydration' },
        { value: CPTcodeCategoryEnum.IVTherapeutic, label: 'IV Therapeutic' },
        { value: CPTcodeCategoryEnum.OtherPsychotherapy, label: 'Other Psychotherapy' },
        { value: CPTcodeCategoryEnum.PhysicalMedicineAndRehabilitation, label: 'Physical Medicine and Rehabilitation' },
        { value: CPTcodeCategoryEnum.PsychotherapyServicesAndProcedures, label: 'Psychotherapy Services and Procedures' },
        { value: CPTcodeCategoryEnum.TelephonicFollowUp, label: 'Telephonic Follow Up' },
    ];

    const handleSubmit = () => {
        if (!props.selectedCPTCode) {
            return;
        }
        dispatch(saveCPTCode(props.selectedCPTCode));
        props.onClose();
    };

    return (
        <Dialog open={props.open} onClose={props.onClose} fullWidth maxWidth="lg">
            <DialogTitle>{props.title} CPT Code</DialogTitle>
            <DialogContent>
                <Form>
                    <Grid container spacing={2} my={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                label="Code"
                                value={props.selectedCPTCode?.Code || ''}
                                onChange={(e) => props.setSelectedCPTCode({ ...props.selectedCPTCode, Code: e.target.value })}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth>
                                <InputLabel id="cpt-code-category-label">CPT Code Category</InputLabel>
                                <Select
                                    fullWidth
                                    label="CPT Code Category"
                                    value={CPTcodeCategories.find((category) => category.value === props.selectedCPTCode?.CPTcodeCategory)?.value ?? ''}
                                    onChange={(e) => {
                                        const selectedCategory = Number(e.target.value);
                                        props.setSelectedCPTCode({ ...props.selectedCPTCode, CPTcodeCategory: selectedCategory });
                                    }}
                                >
                                    {CPTcodeCategories.map((category, index) => (
                                        <MenuItem key={category.value + '_' + index} value={category.value}>
                                            {category.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                label="Description"
                                value={props.selectedCPTCode?.Description || ''}
                                onChange={(e) => props.setSelectedCPTCode({ ...props.selectedCPTCode, Description: e.target.value })}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                label="Medicare Rate"
                                type="number"
                                value={
                                    props.selectedCPTCode?.MedicareRate !== undefined && props.selectedCPTCode?.MedicareRate !== null
                                        ? props.selectedCPTCode.MedicareRate.toString()
                                        : ''
                                }
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                }}
                                onChange={(e) => {
                                    const rawValue = e.target.value;
                                    const numericValue = rawValue.replace(/[^0-9.]/g, '');
                                    const parsed = parseFloat(numericValue);
                                    if (!isNaN(parsed)) {
                                        props.setSelectedCPTCode({ ...props.selectedCPTCode, MedicareRate: parsed });
                                    }
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                label="Medicaid Rate"
                                type="number"
                                value={
                                    props.selectedCPTCode?.MedicaidRate !== undefined && props.selectedCPTCode?.MedicaidRate !== null
                                        ? props.selectedCPTCode.MedicaidRate.toString()
                                        : ''
                                }
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                }}
                                onChange={(e) => {
                                    const rawValue = e.target.value;
                                    const numericValue = rawValue.replace(/[^0-9.]/g, '');
                                    const parsed = parseFloat(numericValue);
                                    if (!isNaN(parsed)) {
                                        props.setSelectedCPTCode({ ...props.selectedCPTCode, MedicaidRate: parsed });
                                    }
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth>
                                <InputLabel id="has-multi-unit-label">Has Multi Unit</InputLabel>
                                <Select
                                    fullWidth
                                    label="Has Multi Unit"
                                    value={props.selectedCPTCode?.HasMultiUnit ? 'Yes' : 'No'}
                                    onChange={(e) => props.setSelectedCPTCode({ ...props.selectedCPTCode, HasMultiUnit: e.target.value === 'Yes' })}
                                >
                                    <MenuItem value={'Yes'}>Yes</MenuItem>
                                    <MenuItem value={'No'}>No</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth>
                                <FormLabel>Place of encounter</FormLabel>
                                <FormGroup row>
                                    {placesOfEncounters.map((place, index) => (
                                        <FormControlLabel
                                            key={place.Name + '_' + index}
                                            control={
                                                <Checkbox
                                                    checked={props.selectedCPTCode?.PlaceOfEncounterIds?.includes(place.Id) || false}
                                                    onChange={(e) => {
                                                        const currentPlaces = props.selectedCPTCode?.PlaceOfEncounterIds || [];
                                                        if (e.target.checked) {
                                                            props.setSelectedCPTCode({
                                                                ...props.selectedCPTCode,
                                                                PlaceOfEncounterIds: [...currentPlaces, place.Id],
                                                            });
                                                        } else {
                                                            props.setSelectedCPTCode({
                                                                ...props.selectedCPTCode,
                                                                PlaceOfEncounterIds: currentPlaces.filter((p) => p !== place.Id),
                                                            });
                                                        }
                                                    }}
                                                />
                                            }
                                            label={place.Name}
                                        />
                                    ))}
                                </FormGroup>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth>
                                <Autocomplete
                                    multiple
                                    options={modifiers}
                                    getOptionLabel={(opt) => `${opt.Code} — ${opt.Description}`}
                                    sx={{ mt: 1 }}
                                    value={modifiers.filter((m) => props.selectedCPTCode?.AllowedModifierCodes?.includes(m.Code))}
                                    onChange={(_, selected) =>
                                        props.setSelectedCPTCode({ ...props.selectedCPTCode, AllowedModifierCodes: selected.map((m) => m.Code) })
                                    }
                                    renderInput={(params) => <TextField {...params} label="Allowed Modifiers" size="small" fullWidth />}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                </Form>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button variant="outlined" sx={{ minWidth: '120px' }} onClick={props.onClose}>
                    Cancel
                </Button>
                <Button variant="contained" sx={{ minWidth: '120px' }} onClick={handleSubmit} disabled={!props.selectedCPTCode}>
                    Ok
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const CPTCodes = ({ canEdit }: { canEdit: boolean }) => {
    const dispatch = useAppDispatch();
    const { allCPTCodes, placesOfEncounters, selectedIds, pendingCPTCodeChanges } = useSelector((state: RootState) => state.adminCPTCodesSlice);
    const [showEditCPTCode, setShowEditCPTCode] = useState(false);
    const [showDeleted, setShowDeleted] = useState(false);
    const [title, setTitle] = useState('');
    const [selectedCPTCode, setSelectedCPTCode] = useState<CPTcode | null>(null);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);

    const handleAddNewCPTCode = () => {
        setTitle('Add New');
        setSelectedCPTCode(new CPTcode());
        setShowEditCPTCode(true);
    };

    const handleCloseEditCPTCode = () => {
        setShowEditCPTCode(false);
    };

    const editCPTCodeHandler = (params: any) => {
        const hasPendingChange = pendingCPTCodeChanges.some((c: any) => c.CPTCodeId === params.row.Id);
        if (hasPendingChange) {
            alert('This CPT code already has a pending change awaiting approval.');
            return;
        }

        setTitle('Edit');
        setSelectedCPTCode(params.row);

        setShowEditCPTCode(true);
    };

    const deleteCPTCodeHandler = (params: any) => {
        // dispatch(openDeleteContactHandler(params.row));
        setSelectedCPTCode(params.row);
        setShowConfirmDelete(true);
    };

    const handleConfirmDelete = () => {
        const updatedCPTCode = { ...selectedCPTCode, IsDeleted: true } as CPTcode;
        setSelectedCPTCode(updatedCPTCode);
        dispatch(saveCPTCode(updatedCPTCode));
        setShowConfirmDelete(false);
    };

    useEffect(() => {
        dispatch(fetchAllCPTCodes());
        dispatch(fetchActiveModifiers());
    }, [dispatch]);

    const columns: GridColDef[] = [
        {
            field: 'Code',
            headerName: 'Code',
            minWidth: 120,
        },
        {
            field: 'CPTcodeCategory',
            headerName: 'CPT Code Category',
            minWidth: 160,
            valueGetter: (value, row) => `${CPTcodeCategoryEnum[row.CPTcodeCategory]}`,
        },
        {
            field: 'Description',
            headerName: 'Description',
            minWidth: 160,
            flex: 1,
        },
        {
            field: 'MedicareRate',
            headerName: 'Medicare Rate',
            minWidth: 120,
            valueGetter: (value, row) => `$${row.MedicareRate.toFixed(2)}`,
        },
        {
            field: 'MedicaidRate',
            headerName: 'Medicaid Rate',
            minWidth: 120,
            valueGetter: (value, row) => `$${row.MedicaidRate.toFixed(2)}`,
        },
        {
            field: 'HasMultiUnit',
            headerName: 'Has Multi Unit',
            minWidth: 80,
            valueGetter: (value, row) => `${row.HasMultiUnit ? 'Yes' : 'No'}`,
        },
        {
            field: 'PlaceOfEncounter',
            headerName: 'Place of Encounter',
            minWidth: 160,
            renderCell: (params) => {
                const placeNames = placesOfEncounters.filter((place) => params.row.PlaceOfEncounterIds?.includes(place.Id)).map((place) => place.Name);
                return (
                    <Stack>
                        {placeNames.map((name, index) => (
                            <Typography key={name + '_' + index}>{name}</Typography>
                        ))}
                    </Stack>
                );
            },
        },
        {
            field: 'AllowedModifierCodes',
            headerName: 'Allowed Modifier Codes',
            minWidth: 160,
            renderCell: (params) => {
                const modifierCodes = params.row.AllowedModifierCodes?.join(', ') || '';
                return <Typography>{modifierCodes}</Typography>;
            },
        },
        {
            field: 'action',
            headerName: 'Action',
            sortable: false,
            minWidth: 100,
            flex: 1,
            renderCell: (params) => {
                return params.row.IsDeleted ? (
                    <Typography>Deleted</Typography>
                ) : (
                    <Stack direction="row" spacing={1}>
                        <Tooltip title="Edit Code" placement="bottom">
                            <EditIcon color="primary" style={{ marginLeft: '24px', cursor: 'pointer' }} onClick={() => editCPTCodeHandler(params)} />
                        </Tooltip>
                        <Tooltip title="Delete Code" placement="top">
                            <CancelIcon color="error" style={{ marginLeft: '24px', cursor: 'pointer' }} onClick={() => deleteCPTCodeHandler(params)} />
                        </Tooltip>
                    </Stack>
                );
            },
        },
    ].filter((col) => col.field !== 'action' || canEdit);

    return (
        <Grid container spacing={2}>
            {canEdit && (
                <Grid size={12} display="flex" justifyContent="space-between" alignItems="center">
                    <FormControlLabel control={<Switch checked={showDeleted} onChange={() => setShowDeleted((prev) => !prev)} />} label="Show Deleted" />
                    <Button variant="contained" onClick={handleAddNewCPTCode}>
                        Add CPT Code
                    </Button>
                </Grid>
            )}

            <Grid size={12} height={'75vh'}>
                {canEdit && (
                    <DataGrid
                        rows={showDeleted ? allCPTCodes : allCPTCodes.filter((cptCode) => !cptCode.IsDeleted)}
                        columns={columns}
                        pageSizeOptions={[10]}
                        getRowId={(row) => row.Id}
                        getRowClassName={(params) => (params.row.IsDeleted ? 'deleted-row' : '')}
                        getRowHeight={() => 'auto'}
                        sx={{
                            [`& .${gridClasses.cell}`]: {
                                py: 1,
                            },
                            [`& .${gridClasses.row}.deleted-row`]: {
                                fontStyle: 'italic',
                                color: 'text.disabled',
                            },
                        }}
                    />
                )}
                {!canEdit && (
                    <DataGrid
                        rows={showDeleted ? allCPTCodes : allCPTCodes.filter((cptCode) => !cptCode.IsDeleted)}
                        columns={columns}
                        checkboxSelection
                        pageSizeOptions={[10]}
                        onRowSelectionModelChange={(model) => {
                            dispatch(setSelectedIds(Array.from(model.ids)));
                        }}
                        rowSelectionModel={{ type: 'include', ids: new Set(selectedIds) }}
                        getRowId={(row) => row.Id}
                        getRowClassName={(params) => (params.row.IsDeleted ? 'deleted-row' : '')}
                        getRowHeight={() => 'auto'}
                        sx={{
                            [`& .${gridClasses.cell}`]: {
                                py: 1,
                            },
                            [`& .${gridClasses.row}.deleted-row`]: {
                                fontStyle: 'italic',
                                color: 'text.disabled',
                            },
                        }}
                    />
                )}
            </Grid>
            <EditCPTCodeModal
                open={showEditCPTCode}
                onClose={handleCloseEditCPTCode}
                title={title}
                selectedCPTCode={selectedCPTCode}
                setSelectedCPTCode={setSelectedCPTCode}
            />
            <ConfirmDelete
                open={showConfirmDelete}
                onClose={() => setShowConfirmDelete(false)}
                name={`CPT code: ${selectedCPTCode?.Code}` || ''}
                // delete={() => {
                //     dispatch(saveCPTCode(selectedCPTCode));
                //     setShowConfirmDelete(false);
                // }}
                delete={handleConfirmDelete}
                cancel={() => setShowConfirmDelete(false)}
            />
        </Grid>
    );
};

export default CPTCodes;
